// TTSService.ts
import {Platform} from 'react-native';

// Only import Tts on non-web platforms
let Tts: any = null;
if (Platform.OS !== 'web') {
  // Dynamic import to prevent web bundling issues
  Tts = require('react-native-tts').default;
}

// Define a custom Voice structure that we'll use for web
interface CommonVoice {
  id: string;
  name: string;
  language: string;
  quality: number;
  latency: number;
}

/**
 * Finds the best male English (en-US) voice based on quality and latency.
 * @param voices Array of available voices.
 * @returns The best male en-US voice object or `null` if not found.
 */
const findBestEnUSMaleVoice = async (): Promise<CommonVoice | null> => {
  if (Platform.OS === 'web') {
    // Web implementation
    if (!window.speechSynthesis) return null;

    const voices = window.speechSynthesis.getVoices();
    const enVoices = voices.filter(voice => voice.lang.includes('en'));

    // Prefer Google voices as they tend to be higher quality
    const googleVoice = enVoices.find(voice => voice.name.includes('Google'));
    if (googleVoice) {
      return {
        id: googleVoice.voiceURI,
        name: googleVoice.name,
        language: googleVoice.lang,
        quality: 400,
        latency: 100,
      };
    }

    // Otherwise return any English voice
    if (enVoices.length > 0) {
      const defaultVoice = enVoices[0];
      return {
        id: defaultVoice.voiceURI,
        name: defaultVoice.name,
        language: defaultVoice.lang,
        quality: 300,
        latency: 100,
      };
    }
    return null;
  } else {
    // Native implementation
    if (!Tts) return null;

    const voices = await Tts.voices();
    const voicesList = voices
      .filter(
        (voice: any) => voice.language.includes('en'), // Common male voice patterns
      )
      .sort((a: any, b: any) => {
        // Prioritize higher quality, then lower latency
        if (b.quality !== a.quality) {
          return b.quality - a.quality; // Higher quality first
        }
        return a.latency - b.latency; // Lower latency first
      });

    if (voicesList.length === 0) return null;

    const bestVoice = voicesList[0];
    return {
      id: bestVoice.id,
      name: bestVoice.name,
      language: bestVoice.language,
      quality: bestVoice.quality,
      latency: bestVoice.latency,
    };
  }
};

export interface TTSServiceOptions {
  language?: string; // e.g., "en-US"
  rate?: number; // e.g., 0.5
  pitch?: number; // e.g., 1.0
  ducking?: boolean; // whether to lower other apps volume
  iosVoiceId?: string; // iOS voice ID
  androidVoiceId?: string; // Android voice ID
  webVoiceId?: string; // Web voice ID
}

/** Internal TTS state shape */
interface TTSState {
  isReading: boolean;
  isVoiceLoading: boolean;
  isVoiceError: boolean;
}

export class TTSService {
  private static instance: TTSService | null = null;

  private isReading: boolean = false;
  private isVoiceLoading: boolean = true;
  private isVoiceError: boolean = false;

  // Minimal default options
  private defaultLanguage: string = 'en-US';
  private defaultRate: number = 0.5;
  private defaultPitch: number = 1.0;
  private defaultDucking: boolean = false;
  private defaultIosVoiceId?: string;
  private defaultAndroidVoiceId?: string;
  private defaultWebVoiceId?: string;

  // Web-specific properties
  private webSpeechUtterance: SpeechSynthesisUtterance | null = null;
  private isWeb: boolean = Platform.OS === 'web';

  /**
   * An array of "subscriber" callbacks that we will call
   * whenever our TTS state changes. This approach is effectively
   * a manual event system, but without external libraries.
   */
  private subscribers: Array<(state: TTSState) => void> = [];

  /** Private constructor enforces singleton usage */
  private constructor() {}

  /** Retrieve or create the singleton instance */
  public static getInstance(): TTSService {
    if (!TTSService.instance) {
      TTSService.instance = new TTSService();
    }
    return TTSService.instance;
  }

  /**
   * Initialize the TTS engine once (call in app startup).
   */
  public async init(options: TTSServiceOptions = {}): Promise<void> {
    this.defaultLanguage = options.language ?? 'en-US';
    this.defaultRate = options.rate ?? 0.5;
    this.defaultPitch = options.pitch ?? 1.0;
    this.defaultDucking = options.ducking ?? false;

    this.setVoiceLoading(true);
    this.setVoiceError(false);

    try {
      const voice = await findBestEnUSMaleVoice();

      if (this.isWeb) {
        // Web implementation
        if (!window.speechSynthesis) {
          throw new Error('Speech synthesis not supported in this browser');
        }

        this.defaultWebVoiceId = voice?.id || options.webVoiceId;
        this.webSpeechUtterance = new window.SpeechSynthesisUtterance();
        this.webSpeechUtterance.lang = this.defaultLanguage;
        this.webSpeechUtterance.rate = this.defaultRate * 2; // Web rate is 0-10, RN is 0-1
        this.webSpeechUtterance.pitch = this.defaultPitch;

        // Set up web speech events
        this.addWebSpeechListeners();
      } else {
        // Native implementation
        if (!Tts) {
          throw new Error('TTS is not available on this platform');
        }

        // Optionally request additional data on Android
        this.defaultIosVoiceId = voice?.id || options.iosVoiceId;
        this.defaultAndroidVoiceId = voice?.id || options.androidVoiceId;

        // Wait for TTS engine to be ready
        await Tts.getInitStatus();

        await Tts.setDefaultLanguage(this.defaultLanguage);
        await Tts.setDefaultRate(this.defaultRate);
        await Tts.setDefaultPitch(this.defaultPitch);
        await Tts.setDucking(this.defaultDucking);

        // Setup TTS event listeners
        this.addTtsListeners();
      }

      this.setVoiceLoading(false);
      this.setVoiceError(false);
    } catch (error) {
      this.setVoiceLoading(false);
      this.setVoiceError(true);
    }
  }

  /**
   * Called once, e.g. at app exit. Removes TTS listeners.
   */
  public destroy(): void {
    if (this.isWeb) {
      this.removeWebSpeechListeners();
    } else if (Tts) {
      this.removeTtsListeners();
    }
    // Clear out the singleton
    TTSService.instance = null;
  }

  /** Speak text, optionally override language. */
  public async startReading(text: string, customLanguage?: string) {
    try {
      if (this.isWeb) {
        if (!window.speechSynthesis || !this.webSpeechUtterance) {
          throw new Error('Speech synthesis not available');
        }
      
        // Cancel any ongoing speech
        window.speechSynthesis.cancel();
      
        // Function to chunk text
        const chunkText = (text: string, maxLength = 200) => {
          const chunks = [];
          const sentences = text.split(/[.!?]+/).filter((s: string) => s.trim().length > 0);
          
          let currentChunk = '';
          for (const sentence of sentences) {
            if ((currentChunk + sentence).length > maxLength && currentChunk.length > 0) {
              chunks.push(currentChunk.trim());
              currentChunk = sentence;
            } else {
              currentChunk += (currentChunk ? '. ' : '') + sentence;
            }
          }
          if (currentChunk.trim().length > 0) {
            chunks.push(currentChunk.trim());
          }
          return chunks;
        };
      
        const chunks = chunkText(text, 200); // Adjust chunk size as needed
        let currentChunkIndex = 0;
      
        const speakNextChunk = () => {
          if (currentChunkIndex >= chunks.length) {
            this.setReading(false);
            return;
          }
      
          const utterance = new SpeechSynthesisUtterance(chunks[currentChunkIndex]);
          
          if (customLanguage) {
            utterance.lang = customLanguage;
          } else {
            utterance.lang = this.defaultLanguage;
          }
      
          // Set voice if available
          if (this.defaultWebVoiceId) {
            const voices = window.speechSynthesis.getVoices();
            const selectedVoice = voices.find(v => v.voiceURI === this.defaultWebVoiceId);
            if (selectedVoice) {
              utterance.voice = selectedVoice;
            }
          }
      
          utterance.onend = () => {
            currentChunkIndex++;
            setTimeout(speakNextChunk, 100); // Small delay between chunks
          };
      
          utterance.onerror = (event) => {
            this.setReading(false);
          };
      
          window.speechSynthesis.speak(utterance);
        };
      
        this.setReading(true);
        speakNextChunk();
      } else {
        // Native implementation
        if (!Tts) {
          throw new Error('TTS is not available on this platform');
        }

        if (customLanguage) {
          await Tts.setDefaultLanguage(customLanguage);
        } else {
          await Tts.setDefaultLanguage(this.defaultLanguage);
        }
        const speakOptions: any = {
          iosVoiceId: this.defaultIosVoiceId,
          rate: this.defaultRate,
          androidParams: {
            KEY_PARAM_PAN: 0,
            KEY_PARAM_VOLUME: 1,
            KEY_PARAM_STREAM: 'STREAM_MUSIC',
            voice: this.defaultAndroidVoiceId,
          },
        };

        Tts.speak(text, speakOptions);
      }
    } catch (error) {
      this.setVoiceError(true);
      this.setReading(false);
    }
  }

  /** Stop reading and clear TTS queue */
  public async stopReading() {
    try {
      if (this.isWeb) {
        // Web implementation
        if (window.speechSynthesis) {
          window.speechSynthesis.cancel();
        }
      } else if (Tts) {
        // Native implementation
        await Tts.stop();
      }
    } catch (error) {
    }
    this.setReading(false);
  }

  // Get current states
  public getIsReading() {
    return this.isReading;
  }
  public getIsVoiceLoading() {
    return this.isVoiceLoading;
  }
  public getIsVoiceError() {
    return this.isVoiceError;
  }

  // Subscribe to TTS state changes
  public subscribe(callback: (state: TTSState) => void) {
    // Save callback
    this.subscribers.push(callback);

    // Return an unsubscribe function
    return () => {
      this.subscribers = this.subscribers.filter(fn => fn !== callback);
    };
  }

  // "Private" methods to update local state & notify watchers

  private setReading(val: boolean) {
    this.isReading = val;
    this.notifySubscribers();
  }

  private setVoiceLoading(val: boolean) {
    this.isVoiceLoading = val;
    this.notifySubscribers();
  }

  private setVoiceError(val: boolean) {
    this.isVoiceError = val;
    this.notifySubscribers();
  }

  private notifySubscribers() {
    const snapshot: TTSState = {
      isReading: this.isReading,
      isVoiceLoading: this.isVoiceLoading,
      isVoiceError: this.isVoiceError,
    };
    this.subscribers.forEach(fn => fn(snapshot));
  }

  // Web-specific event listeners
  private addWebSpeechListeners() {
    if (!this.webSpeechUtterance || !window.speechSynthesis) return;

    this.webSpeechUtterance.onstart = this.onWebSpeechStart;
    this.webSpeechUtterance.onend = this.onWebSpeechEnd;
    this.webSpeechUtterance.onerror = this.onWebSpeechError;
    this.webSpeechUtterance.onpause = this.onWebSpeechPause;
    this.webSpeechUtterance.onresume = this.onWebSpeechResume;
  }

  private removeWebSpeechListeners() {
    if (!this.webSpeechUtterance) return;

    this.webSpeechUtterance.onstart = null;
    this.webSpeechUtterance.onend = null;
    this.webSpeechUtterance.onerror = null;
    this.webSpeechUtterance.onpause = null;
    this.webSpeechUtterance.onresume = null;
  }

  private onWebSpeechStart = () => {
    this.setReading(true);
  };

  private onWebSpeechEnd = () => {
    this.setReading(false);
  };

  private onWebSpeechError = (event: SpeechSynthesisErrorEvent) => {
    this.setReading(false);
    this.setVoiceError(true);
  };

  private onWebSpeechPause = () => {
    // Optional: handle pause state if needed
  };

  private onWebSpeechResume = () => {
    // Optional: handle resume state if needed
  };

  // Native-specific event listeners
  private addTtsListeners() {
    if (!Tts) return;

    Tts.addEventListener('tts-start', this.onTtsStart);
    Tts.addEventListener('tts-finish', this.onTtsFinish);
    Tts.addEventListener('tts-cancel', this.onTtsCancel);

    // Some versions also support 'tts-error'
    Tts.addEventListener('tts-error', this.onTtsError);
  }

  private removeTtsListeners() {
    if (!Tts) return;

    if (typeof Tts.removeEventListener === 'function') {
      Tts.removeEventListener('tts-start', this.onTtsStart);
      Tts.removeEventListener('tts-finish', this.onTtsFinish);
      Tts.removeEventListener('tts-cancel', this.onTtsCancel);
      Tts.removeEventListener('tts-error', this.onTtsError);
    } else if (typeof Tts.removeAllListeners === 'function') {
      Tts.removeAllListeners('tts-start');
      Tts.removeAllListeners('tts-finish');
      Tts.removeAllListeners('tts-cancel');
      Tts.removeAllListeners('tts-error');
    }
  }

  private onTtsStart = () => {
    this.setReading(true);
  };
  private onTtsFinish = () => {
    this.setReading(false);
  };
  private onTtsCancel = () => {
    this.setReading(false);
  };
  private onTtsError = (event: any) => {
    this.setReading(false);
    this.setVoiceError(true);
  };
}
